import { Stack, Text, vars } from '@etg/wings';
import { DangerousHtmlContent } from '@eti/components';
import { css, cx, getTextStyles, mediaQueries } from '@eti/styles';
import { CaretDown } from '@phosphor-icons/react';
import { useState, useEffect, useRef } from 'react';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useTranslation } from '@eti/providers';
import { TRACKING_PROPERTIES, useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import { useProductCardContext } from '../../context/ProductCardContext';
import { useLinksAccessibility } from '../../../custom-hooks/useLinkAccessibility';
import { useDynamicIcon } from '../../utils/dynamic-icons/useDynamicIcon';
import { DYNAMIC_ICON } from '../../utils/dynamic-icons/icons';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const labelStyles = css`
  align-items: center;
  background: none;
  border: none;
  color: ${vars.colors.links.default};
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 0;
  text-decoration: underline;
  width: fit-content;

  @media ${mediaQueries.small.only} {
    margin-inline-start: 40px;
  }
`;
const readMoreContentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${mediaQueries.medium.up} {
    max-width: 522px;
  }
`;
const contentStyles = iconContent => css`
  margin-bottom: 8px;

  ul {
    li {
      list-style-position: inside;
      padding-inline-start: 12px;

      &::before {
        background-color: ${vars.colors.text.primary};
      }

      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
  }

  & a {
    color: ${vars.colors.links.default};
    position: relative;

    &.link {
      display: block;
      margin-bottom: 0;
      width: fit-content;
    }

    + p {
      margin-top: 16px;
    }

    &::after {
      bottom: 6px;
      content: ${iconContent !== null && iconContent !== void 0 ? iconContent : ''};
      display: inline-block;
      height: 16px;
      margin-inline-start: 4px;
      position: relative;
      vertical-align: bottom;
      width: 16px;
    }
  }

  @media ${mediaQueries.medium.up} {
    font-size: 0.875rem;
  }
`;
const SmallCardReadMore = _ref => {
  let {
    children,
    customGrid
  } = _ref;
  const {
    t
  } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const rootElement = useRef(null);
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  const {
    readMoreButtonLabel,
    readMoreContent,
    productId,
    productName,
    title
  } = useProductCardContext();
  const {
    sendClickEvent,
    addTrackingEventOnLinks
  } = useProductSnowplowTracking(productName);
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title,
    accordionOpen: isExpanded
  });
  const readLessButtonLabel = t('Products.ReadLess.Link.Label');
  useEffect(() => {
    if (isExpanded) {
      sendClickEvent({
        property: TRACKING_PROPERTIES.READ_MORE
      });
      addTrackingEventOnLinks(rootElement);
    }
  }, [isExpanded, sendClickEvent, addTrackingEventOnLinks]);
  return readMoreContent ? /*#__PURE__*/_jsxs(Stack, {
    className: cx('readMore', customGrid),
    "data-testid": "smallcard-readmore",
    ref: rootElement,
    spacing: 0,
    children: [/*#__PURE__*/_jsxs("button", {
      "aria-controls": String(productId),
      "aria-expanded": isExpanded,
      "aria-label": `${readMoreButtonLabel} ${title}`,
      className: labelStyles,
      "data-testid": "readmore-button",
      onClick: () => setIsExpanded(!isExpanded),
      type: "button",
      children: [/*#__PURE__*/_jsx(Text, {
        children: isExpanded ? readLessButtonLabel : readMoreButtonLabel
      }), /*#__PURE__*/_jsx(motion.div, {
        animate: {
          rotateX: isExpanded ? 180 : 0,
          display: 'flex'
        },
        children: /*#__PURE__*/_jsx(CaretDown, {
          size: 18
        })
      })]
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: isExpanded && /*#__PURE__*/_jsxs(motion.div, {
        animate: {
          height: 'auto'
        },
        className: readMoreContentStyles,
        exit: {
          height: 0
        },
        id: String(productId),
        initial: {
          height: 0,
          overflow: 'hidden',
          marginTop: '16px'
        },
        transition: {
          duration: 0.3
        },
        children: [children && /*#__PURE__*/_jsx("div", {
          children: children
        }), /*#__PURE__*/_jsx(DangerousHtmlContent, {
          className: cx(getTextStyles(), contentStyles(icon), 'text'),
          content: readMoreContent
        })]
      })
    })]
  }) : null;
};
export default SmallCardReadMore;
//# sourceMappingURL=SmallCardReadMore.js.map